import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api.js";

const fmtDate = (iso) =>
  new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(iso));

export default function TenderDetail() {
  const { id } = useParams();
  const [tender, setTender] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .getTender(id)
      .then(setTender)
      .catch((e) => setErr(e.message || "Fehler"));
  }, [id]);

  if (err) {
    return (
      <div className="rounded-md border border-brand-200 bg-brand-50 p-4 text-sm text-brand-800">
        {err}
      </div>
    );
  }
  if (!tender) {
    return <div className="text-ink-500">Wird geladen …</div>;
  }

  return (
    <article className="mx-auto max-w-3xl space-y-6">
      <Link to="/" className="btn-ghost -ml-3">
        ← Zur Übersicht
      </Link>

      <header className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="chip-brand font-mono">
            Score {tender.relevance_score.toFixed(1)}
          </span>
          <span className="chip font-mono">{tender.country || "—"}</span>
          <span className="chip">{tender.source_name}</span>
          {tender.language && (
            <span className="chip font-mono">{tender.language}</span>
          )}
        </div>
        <h1 className="text-2xl font-semibold leading-tight text-ink-900">
          {tender.title}
        </h1>
      </header>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg border border-ink-200 bg-white p-5 text-sm">
        <MetaRow label="Auftraggeber" value={tender.buyer} />
        <MetaRow label="Kategorie" value={tender.category} />
        <MetaRow
          label="Veröffentlicht"
          value={
            tender.publication_date
              ? fmtDate(tender.publication_date)
              : tender.created_at
                ? `${fmtDate(tender.created_at)} (entdeckt)`
                : null
          }
        />
        <MetaRow
          label="Frist"
          value={tender.deadline ? fmtDate(tender.deadline) : null}
        />
        <MetaRow
          label="CPV"
          value={tender.cpv_codes?.length ? tender.cpv_codes.join(", ") : null}
          mono
        />
        <MetaRow label="Source-ID" value={tender.source_id} mono />
      </dl>

      {tender.description && (
        <section>
          <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-600">
            Beschreibung
          </h2>
          <div className="whitespace-pre-wrap rounded-lg border border-ink-200 bg-white p-5 text-sm text-ink-800">
            {tender.description}
          </div>
        </section>
      )}

      {tender.keywords_matched?.length > 0 && (
        <section>
          <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-600">
            Matches
          </h2>
          <div className="flex flex-wrap gap-1.5">
            {tender.keywords_matched.map((k) => (
              <span key={k} className="chip-brand">{k}</span>
            ))}
          </div>
        </section>
      )}

      {tender.original_url && (
        <a
          href={tender.original_url}
          target="_blank"
          rel="noreferrer"
          className="btn-primary"
        >
          Originalausschreibung öffnen ↗
        </a>
      )}
    </article>
  );
}

function MetaRow({ label, value, mono = false }) {
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-wider text-ink-500">
        {label}
      </dt>
      <dd
        className={`mt-0.5 text-ink-900 ${mono ? "font-mono text-xs" : ""}`}
      >
        {value || "—"}
      </dd>
    </div>
  );
}
